import * as signalR from '@microsoft/signalr';
import { getLocalStorageKey } from '../helpers/localStorageHelper';
import { IConversationMessage } from '../interfaces/heavyDutyMachinery/IConversation';
import { IAdminConversationMessage } from '../interfaces/heavyDutyMachinery/IAdminConversation';
import { IHeavyDutyMachineryNotification } from '../interfaces/heavyDutyMachinery/INotification';
import { IBookingConfirmedEvent, IBookingPaymentFailedEvent } from '../interfaces/heavyDutyMachinery/IBooking';
import { IFullMachineryTelemetry } from '../interfaces/heavyDutyMachinery/ITelemetry';

// Ported from Authentication76's RealtimeService.ts (same shape/pattern),
// pointed at HeavyDutyMachineryApi's own Hub instead of Marketplace's —
// see HeavyDutyMachineryHub.cs. No auction-style group join/leave here, since
// HeavyDutyMachinery has no equivalent live-bid feature.
const HUB_PATH = '/hubs/heavy-duty-machinery';
const baseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

let connection: signalR.HubConnection | null = null;
let starting: Promise<void> | null = null;

// Created lazily (not started) so components can call on___() to register
// listeners regardless of whether startRealtimeConnection() has run yet —
// SignalR lets you attach handlers before the connection is started.
const getConnection = (): signalR.HubConnection => {
  if (!connection) {
    connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}${HUB_PATH}`, {
        accessTokenFactory: () => getLocalStorageKey('accessToken') || '',
      })
      .withAutomaticReconnect()
      .build();
  }
  return connection;
};

export const startRealtimeConnection = (): Promise<void> => {
  const conn = getConnection();
  if (conn.state === signalR.HubConnectionState.Connected) return Promise.resolve();
  if (starting) return starting;

  starting = conn
    .start()
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('Realtime connection unavailable, continuing without live updates.', error);
    })
    .finally(() => {
      starting = null;
    });

  return starting;
};

export const stopRealtimeConnection = async (): Promise<void> => {
  if (!connection) return;
  const conn = connection;
  connection = null;
  await conn.stop().catch(() => undefined);
};

export const sendTyping = async (toUserId: string): Promise<void> => {
  const conn = getConnection();
  if (conn.state !== signalR.HubConnectionState.Connected) return;
  await conn.invoke('SendTyping', toUserId).catch(() => undefined);
};

// Tells HeavyDutyMachineryHub's IActiveConversationTracker "I'm looking at this
// conversation right now" / "not anymore" -- ConversationsController checks
// this before firing a NewMessage bell notification, so a peer who already
// has the thread open and visible doesn't get notified about a message
// that's right in front of them. MessageThreadPanel calls these, gated on
// the Page Visibility API (a backgrounded tab should still notify).
export const joinConversation = async (conversationId: string): Promise<void> => {
  const conn = getConnection();
  if (conn.state !== signalR.HubConnectionState.Connected) return;
  await conn.invoke('JoinConversation', Number(conversationId)).catch(() => undefined);
};

export const leaveConversation = async (): Promise<void> => {
  const conn = getConnection();
  if (conn.state !== signalR.HubConnectionState.Connected) return;
  await conn.invoke('LeaveConversation').catch(() => undefined);
};

// Same idea as joinConversation/leaveConversation above, for the
// admin-support thread (see HeavyDutyMachineryHub.JoinAdminConversation/
// LeaveAdminConversation) -- kept separate rather than reusing those so an
// admin conversation id can never collide with a booking conversation id in
// the backend's tracker.
export const joinAdminConversation = async (conversationId: string): Promise<void> => {
  const conn = getConnection();
  if (conn.state !== signalR.HubConnectionState.Connected) return;
  await conn.invoke('JoinAdminConversation', Number(conversationId)).catch(() => undefined);
};

export const leaveAdminConversation = async (): Promise<void> => {
  const conn = getConnection();
  if (conn.state !== signalR.HubConnectionState.Connected) return;
  await conn.invoke('LeaveAdminConversation').catch(() => undefined);
};

const subscribe = <T,>(eventName: string, callback: (payload: T) => void): (() => void) => {
  const conn = getConnection();
  const handler = (payload: T) => callback(payload);
  conn.on(eventName, handler);
  return () => conn.off(eventName, handler);
};

export const onReceiveMessage = (callback: (message: IConversationMessage) => void) =>
  subscribe('ReceiveMessage', callback);

// Distinct event name from ReceiveMessage above -- see
// AdminConversationsController.SendMessageInternal, which pushes admin
// replies here instead, keeping the two chat features' payload shapes (and
// any future divergence between them) unambiguous on the wire.
export const onReceiveAdminMessage = (callback: (message: IAdminConversationMessage) => void) =>
  subscribe('ReceiveAdminMessage', callback);

export const onReceiveNotification = (callback: (notification: IHeavyDutyMachineryNotification) => void) =>
  subscribe('ReceiveNotification', callback);

export const onUserTyping = (callback: (fromUserId: string) => void) => subscribe('UserTyping', callback);

// Pushed once the Stripe webhook (or the no-payment-needed path) actually
// confirms a Booking -- see BookingConfirmationHelper on the backend. The
// booking-confirming waiting screen is the one subscriber today, filtering
// on its own bookingId (nothing stops another booking's event from also
// arriving here if the guest has more than one in flight).
export const onBookingConfirmed = (callback: (event: IBookingConfirmedEvent) => void) =>
  subscribe('BookingConfirmed', callback);

export const onBookingPaymentFailed = (callback: (event: IBookingPaymentFailedEvent) => void) =>
  subscribe('BookingPaymentFailed', callback);

// Pushed by TelemetryIngestionService on every reading -- once to the
// machine's own host (always, whatever its booking state) and, while a
// booking is currently in progress, once more to that booking's guest, each
// already filtered server-side to what that recipient is allowed to see
// (see ITelemetryVisibilityPolicy) -- always the IFullMachineryTelemetry
// shape here, since this event is never sent to a Public-level caller.
// payload.machineryId is what tells apart which of a host's several
// machines (if more than one has active telemetry at once) a given event
// belongs to.
export const onTelemetryUpdate = (callback: (payload: IFullMachineryTelemetry) => void) =>
  subscribe('TelemetryUpdate', callback);
